/**
 * LOT 402 — Feature flags publics.
 *
 * Lecture côté client uniquement (variables d'environnement React au build).
 * Aucun secret. Aucun token.
 *
 * `PUBLIC_SPV_OFFER_ENABLED` :
 *   - `true` ou variable absente (par défaut) : l'offre SPV publique reste
 *     visible (création SPV, intake, CTA homepage, nav, footer).
 *   - `false` explicite : tout ce qui est public/SPV est masqué (route SPV →
 *     page neutre, CTA homepage retirés, sitemap nettoyé, navigation header
 *     retirée).
 *
 * ⚠️ LOT Public-SPV bis (Daniel 17/06/2026) : logique inversée pour devenir
 * **robuste en production**. Depuis la signature du protocole partenaire,
 * l'offre est publique durablement. Sur Vercel/prod, si la variable d'env
 * n'est pas définie (cas par défaut), elle est `undefined` ; on doit donc
 * traiter cela comme « visible », et n'occulter QUE si une opération
 * explicitement bascule la variable à `false` (kill-switch).
 *
 * NB : ces flags sont lus AU BUILD côté React (pas d'évaluation runtime).
 * Tout changement nécessite un redéploiement Vercel.
 */
const FALSY = new Set(['false', '0', 'no', 'off']);

/**
 * Retourne `true` SAUF si la variable d'env est explicitement falsy.
 * Variable absente / vide / undefined → considéré comme « activé ».
 */
function readBoolFlagDefaultOn(envValue) {
  if (envValue == null || envValue === '') return true;
  return !FALSY.has(String(envValue).trim().toLowerCase());
}

/**
 * @returns {boolean} true si la page publique SPV doit être visible
 */
export function isPublicSpvOfferEnabled() {
  return readBoolFlagDefaultOn(process.env.REACT_APP_PUBLIC_SPV_OFFER_ENABLED);
}

/**
 * LOT 402-B — Couvre l'intake sponsor / porteur de projet public.
 *
 * Sémantique :
 *   - true  : le site public peut afficher des CTA "soumettre un projet",
 *             "devenir sponsor", "porteurs de projets bienvenus", etc.
 *   - false : tout wording d'acquisition publique de sponsors/porteurs est masqué.
 *
 * Implémentation : alias du même flag SPV (le périmètre est identique
 * pour Daniel — ouvrir publiquement à de nouveaux projets/sponsors).
 * Le séparer en 2 flags distincts est faisable plus tard si besoin.
 */
export function isPublicProjectSponsorIntakeEnabled() {
  // Daniel a validé : un seul flag couvre les deux périmètres
  // (option « REACT_APP_PUBLIC_SPV_OFFER_ENABLED=false couvre aussi les CTA sponsor / porteurs »).
  return isPublicSpvOfferEnabled();
}

/**
 * @returns {object} snapshot lisible pour debug (pas de secrets)
 */
export function getFeatureFlagsSnapshot() {
  return {
    public_spv_offer_enabled: isPublicSpvOfferEnabled(),
    public_project_sponsor_intake_enabled: isPublicProjectSponsorIntakeEnabled(),
  };
}
